import {
  BasicDataGrid,
  DataGridRef,
  ExternalLink,
  getPageStyles,
  Page,
  useFlags,
} from "@inductiveautomation/ws-components";
import { useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import AccountAppBar from "../../components/AccountAppBar/AccountAppBar";
import {
  certificationHeadings,
  courseHeadings,
  credentialHeadings,
  trainingBreadcrumb,
} from "./Training.data";

import {
  useGetTestPricingQuery,
  useGetTrainingQuery,
} from "./api/training.api";
import { AccountFlags } from "../../featureFlags.ts";
import { useCurrentUser } from "../../hooks/currentUser.ts";
import { getTrainingStyles } from "./_training.styles.ts";
import {
  getCoreAttributes,
  getCredentialAttributes,
  getCurrentCourses,
  getGoldAttributes,
  retrieveDerivedStatesFromCards,
} from "./data/Training.current";
import { useMockResponse } from "./mocks/useMockResponse";
import { urlForAction } from "./Training.actions";
import TrainingDialogs from "./Training.dialogs";
import TrainingDrawer from "./Training.drawer";
import { isDialogAction, isLinkAction } from "./Training.typeGuards";
import {
  Certification,
  CertificationActionCallback,
  CertificationActionKind,
  CertificationDialogAction,
  CertificationType,
} from "./Training.types";

const Training = () => {
  const { t } = useTranslation();
  const { tsAccountIuElectiveCoursesProgressFe } = useFlags<AccountFlags>();
  const { data, isLoading } = useGetTrainingQuery();

  const { data: corePricingData } = useGetTestPricingQuery({ level: "core" });
  const { data: goldPricingData } = useGetTestPricingQuery({ level: "gold" });

  const { user, isLoading: userIsLoading } = useCurrentUser();
  const { classes: pageClasses } = getPageStyles();
  const { classes: trainingClasses } = getTrainingStyles();

  const { response } = useMockResponse();
  const [selectedCertification, setSelectedCertification] = useState<
    Certification | undefined
  >();

  const [certificationTypeForPurchase, setCertificationTypeForPurchase] =
    useState<CertificationType | undefined>();
  const [dialogAction, setDialogAction] = useState<
    CertificationDialogAction | undefined
  >();

  const coreDataGridRef = useRef<DataGridRef>(null);
  const goldDataGridRef = useRef<DataGridRef>(null);
  const credentialDataGridRef = useRef<DataGridRef>(null);
  const electiveDataGridRef = useRef<DataGridRef>(null);
  const employeeDataGridRef = useRef<DataGridRef>(null);

  const clearDataGrids = (except?: string) => {
    if (except !== "CREDENTIAL")
      credentialDataGridRef.current?.clearCheckedAndSelected();

    if (except !== "CORE") coreDataGridRef.current?.clearCheckedAndSelected();

    if (except !== "GOLD") goldDataGridRef.current?.clearCheckedAndSelected();

    // wire these up later
    // if (except !== "ELECTIVE")
    //   electiveDataGridRef.current?.clearCheckedAndSelected();
    // if (except !== "EMPLOYEE")
    //   employeeDataGridRef.current?.clearCheckedAndSelected();
  };

  const selectCertification = (rowId: string | undefined) => {
    if (rowId === "CREDENTIAL") {
      setSelectedCertification(currentCredential.certification);
    } else if (rowId === "CORE") {
      setSelectedCertification(currentCoreCertification.certification);
    } else if (rowId === "GOLD") {
      setSelectedCertification(currentGoldCertification.certification);
    }
    clearDataGrids(rowId);
  };

  const onAction: CertificationActionCallback = (
    actionId: CertificationActionKind,
    options?: {
      certificationId?: string;
      certificationPurchaseType?: CertificationType;
    }
  ) => {
    if (isDialogAction(actionId)) {
      if (options?.certificationPurchaseType) {
        setCertificationTypeForPurchase(options.certificationPurchaseType);
      }
      setDialogAction(actionId);
      return;
    }

    if (isLinkAction(actionId)) {
      window.open(urlForAction[actionId]);
      return;
    }

    const active = response.current.find(
      (certification) => certification.type === options?.certificationId
    );
    if (!active) return;

    if (actionId === "download-badge" && options?.certificationId) {
      console.log("download badge");
      // void downloadBadge({ certId: certificationId });
    } else if (
      actionId === "download-certificate" &&
      options?.certificationId
    ) {
      // void downloadCertificate({ certId: certificationId });
      console.log("download certificate");
    }
  };

  const closeDrawer = () => {
    setSelectedCertification(undefined);
    clearDataGrids();
  };

  const { current: currentCredential, footerProps: credentialFooterProps } =
    getCredentialAttributes(t, data, onAction);

  const {
    current: currentCoreCertification,
    footerProps: coreCertificationFooterProps,
  } = getCoreAttributes(t, data?.certifications, onAction, corePricingData);

  const {
    current: currentGoldCertification,
    footerProps: goldCertificationFooterProps,
  } = getGoldAttributes(t, data?.certifications, onAction, goldPricingData);

  const { current: currentElectiveCourses, footerProps: electiveFooterProps } =
    getCurrentCourses(t, response, onAction, "ELECTIVE");
  const { current: currentEmployeeCourses, footerProps: employeeFooterProps } =
    getCurrentCourses(t, response, onAction, "EMPLOYEE");

  const {
    corePurchaseInActionMenu,
    coreCertificationEmpty,
    goldPurchaseInActionMenu,
  } = retrieveDerivedStatesFromCards({
    currentCoreCertification,
    currentGoldCertification,
  });

  return (
    <>
      <AccountAppBar title={[trainingBreadcrumb(t)]} />

      {!isLoading && !userIsLoading && (
        <Page
          className={dialogAction ? pageClasses.hideForPrint : ""}
          sections={[
            {
              heading: t("training.core.title"),
              description: (
                <Trans
                  i18nKey="training.core.description"
                  components={{
                    tag: (
                      <ExternalLink
                        href={urlForAction["view-core-study-guide"]}
                      />
                    ),
                  }}
                ></Trans>
              ),
              children: (
                <BasicDataGrid
                  testId="core-certification-data-grid"
                  ref={coreDataGridRef}
                  headings={certificationHeadings(t)}
                  showHeadings={!!currentCoreCertification.certification}
                  enableSorting={false}
                  labels={{
                    singular_item_name: t("training.singular_item_name"),
                    plural_item_name: t("training.plural_item_name"),
                  }}
                  data={[currentCoreCertification.card]}
                  onSelection={selectCertification}
                  footerProps={coreCertificationFooterProps}
                  {...(corePricingData?.creditsAvailable?.length &&
                  (corePurchaseInActionMenu || coreCertificationEmpty)
                    ? {
                        alertProps: {
                          description: (
                            <span>
                              {t(
                                "training.core.free_certification.alert_description"
                              )}{" "}
                              <a
                                className={trainingClasses.alertHref}
                                href="https://inductiveautomation.com/blog/accelerate-your-ignition-journey-with-a-free-certification-test"
                                target="_blank"
                              >
                                {t(
                                  "training.core.free_certification.learn_more"
                                )}
                              </a>
                            </span>
                          ),
                          variant: "info",
                          hasCloseButton: false,
                          closeButtonEventHandler: () => undefined,
                          id: "core-credits-available-alert",
                          actions: [
                            {
                              label: t(
                                "training.core.free_certification.action_label"
                              ),
                              onClick: () =>
                                onAction("purchase-test", {
                                  certificationPurchaseType: "CORE",
                                }),
                            },
                          ],
                        },
                      }
                    : {})}
                />
              ),
            },
            {
              heading: t("training.gold.title"),
              description: (
                <Trans
                  i18nKey="training.gold.description"
                  components={{
                    tag: (
                      <ExternalLink
                        href={urlForAction["view-gold-study-guide"]}
                      />
                    ),
                  }}
                ></Trans>
              ),
              children: (
                <BasicDataGrid
                  testId="gold-certification-data-grid"
                  ref={goldDataGridRef}
                  headings={certificationHeadings(t)}
                  showHeadings={!!currentGoldCertification.certification}
                  enableSorting={false}
                  labels={{
                    singular_item_name: t("training.singular_item_name"),
                    plural_item_name: t("training.plural_item_name"),
                  }}
                  data={[currentGoldCertification.card]}
                  onSelection={selectCertification}
                  footerProps={goldCertificationFooterProps}
                  {...(goldPricingData?.creditsAvailable?.length &&
                  (goldPurchaseInActionMenu ||
                    !goldCertificationFooterProps.buttonProps?.disabled)
                    ? {
                        alertProps: {
                          description: (
                            <span>
                              {t(
                                "training.gold.free_certification.alert_description"
                              )}{" "}
                              <a
                                className={trainingClasses.alertHref}
                                href="https://inductiveautomation.com/blog/accelerate-your-ignition-journey-with-a-free-certification-test"
                                target="_blank"
                              >
                                {t(
                                  "training.gold.free_certification.learn_more"
                                )}
                              </a>
                            </span>
                          ),
                          variant: "info",
                          hasCloseButton: false,
                          closeButtonEventHandler: () => undefined,
                          id: "gold-credits-available-alert",
                          actions: [
                            {
                              label: t(
                                "training.gold.free_certification.action_label"
                              ),
                              onClick: () =>
                                onAction("purchase-test", {
                                  certificationPurchaseType: "GOLD",
                                }),
                            },
                          ],
                        },
                      }
                    : {})}
                />
              ),
            },
            {
              heading: t("training.credential.title"),
              description: (
                <Trans
                  i18nKey="training.credential.description"
                  components={{
                    tag: (
                      <ExternalLink
                        href={urlForAction["visit-inductive-university"]}
                      />
                    ),
                  }}
                ></Trans>
              ),
              children: (
                <BasicDataGrid
                  testId="credential-data-grid"
                  ref={credentialDataGridRef}
                  headings={credentialHeadings(t)}
                  showHeadings={!!currentCredential.certification}
                  enableSorting={false}
                  labels={{
                    singular_item_name: t("training.singular_item_name"),
                    plural_item_name: t("training.plural_item_name"),
                  }}
                  data={[currentCredential.card]}
                  onSelection={selectCertification}
                  footerProps={credentialFooterProps}
                />
              ),
            },
            ...(tsAccountIuElectiveCoursesProgressFe
              ? [
                  {
                    heading: t("training.elective.title"),
                    description: (
                      <Trans
                        i18nKey="training.elective.description"
                        components={{
                          tag: (
                            <ExternalLink
                              href={urlForAction["visit-inductive-university"]}
                            />
                          ),
                        }}
                      ></Trans>
                    ),
                    children: (
                      <BasicDataGrid
                        testId="elective-data-grid"
                        ref={electiveDataGridRef}
                        headings={courseHeadings(t)}
                        showHeadings={
                          currentElectiveCourses[0]?.id !==
                          "certification-card-empty-elective"
                        }
                        labels={{
                          singular_item_name: t(
                            "training.elective.singular_item_name"
                          ),
                          plural_item_name: t(
                            "training.elective.plural_item_name"
                          ),
                        }}
                        data={currentElectiveCourses}
                        onSelection={selectCertification}
                        footerProps={electiveFooterProps}
                      />
                    ),
                  },
                ]
              : []),
            ...(response.canViewEmployeeCourses
              ? [
                  {
                    heading: t("training.employee.title"),
                    description: (
                      <Trans
                        i18nKey="training.employee.description"
                        components={{
                          tag: (
                            <ExternalLink
                              href={urlForAction["visit-inductive-university"]}
                            />
                          ),
                        }}
                      ></Trans>
                    ),
                    children: (
                      <BasicDataGrid
                        testId="employee-data-grid"
                        ref={employeeDataGridRef}
                        headings={courseHeadings(t)}
                        showHeadings={
                          currentEmployeeCourses[0]?.id !==
                          "certification-card-empty-elective"
                        }
                        labels={{
                          singular_item_name: t(
                            "training.employee.singular_item_name"
                          ),
                          plural_item_name: t(
                            "training.employee.plural_item_name"
                          ),
                        }}
                        data={currentEmployeeCourses}
                        onSelection={selectCertification}
                        footerProps={employeeFooterProps}
                      />
                    ),
                  },
                ]
              : []),
          ]}
        />
      )}

      <TrainingDrawer
        selectedCertification={selectedCertification}
        trainingData={data}
        onAction={onAction}
        onClose={closeDrawer}
        canPurchaseOnline={
          (selectedCertification?.type === "CORE" &&
            !!corePricingData?.canPurchaseOnline) ||
          (selectedCertification?.type === "GOLD" &&
            !!goldPricingData?.canPurchaseOnline) ||
          false
        }
      />

      <TrainingDialogs
        user={user}
        certificationType={certificationTypeForPurchase}
        openTestUrl={currentCoreCertification.certification?.testUrl}
        action={dialogAction}
        onAction={onAction}
        onClose={() => {
          setDialogAction(undefined);
        }}
      />
    </>
  );
};

export default Training;
